import React, { useState } from 'react';
import { Card, Button, Input, Alert, Typography, Checkbox } from 'antd';
import { SafetyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

interface TwoFactorLoginProps {
  onVerify: (token: string, extendedExpiry?: boolean) => Promise<void>;
  onBack: () => void;
  isLoading?: boolean;
  error?: string;
  isAdmin?: boolean;
}

export const TwoFactorLogin: React.FC<TwoFactorLoginProps> = ({
  onVerify,
  onBack,
  isLoading = false,
  error,
  isAdmin = false,
}) => {
  const [token, setToken] = useState('');
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [extendedExpiry, setExtendedExpiry] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (token.trim()) {
      await onVerify(token.trim(), extendedExpiry);
    }
  };

  const handleTokenChange = (value: string) => {
    if (isBackupCode) {
      // Backup codes are 8 characters, alphanumeric
      setToken(value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8));
    } else {
      // TOTP codes are 6 digits
      setToken(value.replace(/\D/g, '').slice(0, 6));
    }
  };

  const toggleBackupCode = () => {
    setIsBackupCode(!isBackupCode);
    setToken('');
  };

  const { Title, Text } = Typography;

  return (
    <Card style={{ maxWidth: 400, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <SafetyOutlined style={{ fontSize: '3rem', color: '#F59E0B', marginBottom: '1rem' }} />
        <Title level={2}>Two-Factor Authentication</Title>
        <Text type="secondary">
          {isBackupCode 
            ? 'Enter one of your backup codes'
            : 'Enter the 6-digit code from your authenticator app'
          }
        </Text>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.5rem' }}>
          <Input
            type="text"
            placeholder={isBackupCode ? 'ABC12345' : '000000'}
            value={token}
            onChange={(e) => handleTokenChange(e.target.value)}
            style={{ 
              textAlign: 'center', 
              fontSize: '1.5rem', 
              fontFamily: 'monospace',
              height: '48px'
            }}
            maxLength={isBackupCode ? 8 : 6}
            autoComplete="one-time-code"
            autoFocus
          />
          
          {error && (
            <Alert
              message={error}
              type="error"
              icon={<ExclamationCircleOutlined />}
              style={{ marginTop: '1rem' }}
            />
          )}
        </div>

        {isAdmin && (
          <div style={{ marginBottom: '1.5rem' }}>
            <Checkbox 
              checked={extendedExpiry} 
              onChange={(e) => setExtendedExpiry(e.target.checked)}
            >
              Keep me signed in for 30 days
            </Checkbox>
          </div>
        )}

        <div style={{ marginBottom: '2rem' }}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isLoading || token.length < (isBackupCode ? 8 : 6)}
            loading={isLoading}
            block
            size="large"
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <Button
            type="link"
            onClick={toggleBackupCode}
            size="small"
          >
            {isBackupCode 
              ? 'Use authenticator code instead'
              : 'Use backup code instead'
            }
          </Button>
          <br />
          <Button
            type="link"
            onClick={onBack}
            size="small"
            style={{ color: '#8c8c8c' }}
          >
            Back to login
          </Button>
        </div>

        <Alert
          message={isBackupCode
            ? 'Backup codes are 8-character codes you saved when setting up 2FA. Each can only be used once.'
            : 'Open your authenticator app and enter the 6-digit code for BrandScience.ai.'
          }
          type="info"
          showIcon={false}
          style={{ fontSize: '12px' }}
        />
      </form>
    </Card>
  );
};